import { Proposal } from 'entities/Proposal/model/types/ProposalSchema';
import { Vendor, VendorActivationStatus } from 'entities/Vendors';

/**
 * Determines if an item is available to be added to property catalog.
 * An item is available if it has at least one proposal where the supplier
 * has product ordering enabled (VERIFIED status).
 *
 * NOTE: Renovations are not currently supported. When renovations feature
 * is added, this function will need to check both operationsAccountStatus
 * and renovationsAccountStatus (when renovationEnabled is true).
 */
export const isItemAvailable = (
    proposals: Proposal[] | undefined,
    vendorsMap: Record<string, Vendor> | null | undefined,
): boolean => {
    if (!proposals || !vendorsMap || proposals.length === 0) {
        return false;
    }

    return proposals.some((proposal) => {
        const vendor = vendorsMap[proposal.seller.uid];
        if (!vendor) {
            return false;
        }

        return (
            vendor.operationsAccountStatus === VendorActivationStatus.VERIFIED
        );
    });
};

/**
 * Gets the activation status for a supplier from a proposal.
 *
 * NOTE: Renovations are not currently supported. When renovations feature
 * is added, this function will need to check renovationsAccountStatus
 * (when renovationEnabled is true) as an alternative path to VERIFIED status.
 */
export const getSupplierActivationStatus = (
    proposal: Proposal,
    vendorsMap: Record<string, Vendor> | null | undefined,
): VendorActivationStatus | null => {
    if (!vendorsMap) {
        return null;
    }

    const vendor = vendorsMap[proposal.seller.uid];
    if (!vendor) {
        return null;
    }

    return vendor.operationsAccountStatus;
};

/**
 * Determines if a supplier should show the "Enable product ordering" button.
 * Shows for suppliers that are not in vendor list OR not verified.
 * Does NOT show for Action Required (INVALID) status.
 *
 * NOTE: Renovations are not currently supported. When renovations feature
 * is added, this function will need to check both operationsAccountStatus
 * and renovationsAccountStatus (when renovationEnabled is true) to determine
 * if the supplier is verified.
 */
export const shouldShowEnableProductOrderingButton = (
    proposal: Proposal,
    vendorsMap: Record<string, Vendor> | null | undefined,
): boolean => {
    if (!vendorsMap) {
        // Vendors not loaded yet - don't show button to avoid misrouting to Add Vendor
        return false;
    }

    const vendor = vendorsMap[proposal.seller.uid];
    if (!vendor) {
        // Supplier not in vendor list - show button
        return true;
    }

    // Don't show for Action Required or Verifying status
    if (vendor.operationsAccountStatus === VendorActivationStatus.INVALID) {
        return false;
    }

    if (vendor.operationsAccountStatus === VendorActivationStatus.VERIFYING) {
        return false;
    }

    const isOperationsVerified =
        vendor.operationsAccountStatus === VendorActivationStatus.VERIFIED;

    // Show button if not verified
    return !isOperationsVerified;
};
